import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Check, AlertCircle, KeyRound } from 'lucide-react';
import { useOptionalVmClient } from '@/api/context';
import { cn } from '@/lib/utils';
import { isPaperThemeActive } from '@/hooks/useThemeFlag';

function ProviderIcon({
  iconUrl,
  displayName,
  paper,
}: {
  iconUrl: string;
  displayName: string;
  paper?: boolean;
}) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <KeyRound
        className={cn('h-[22px] w-[22px]', paper ? 'text-[var(--fg-3)]' : 'text-zinc-400')}
      />
    );
  }

  return (
    <img
      src={iconUrl}
      alt={displayName}
      className={cn('h-5 w-5', paper ? '' : 'brightness-0 invert')}
      onError={() => setFailed(true)}
    />
  );
}

interface OAuthLoginButtonProps {
  sessionId: string;
  provider: string;
  displayName: string;
  iconUrl: string;
  authStartUrl: string;
  status: 'pending' | 'success' | 'error';
  onAuthComplete: (sessionId: string, provider: string) => void;
}

export const OAuthLoginButton = memo(function OAuthLoginButton({
  sessionId,
  provider,
  displayName,
  iconUrl,
  authStartUrl,
  status,
  onAuthComplete,
}: OAuthLoginButtonProps) {
  const vmClient = useOptionalVmClient();
  const [loading, setLoading] = useState(false);
  const [authDone, setAuthDone] = useState(status === 'success');
  const [authError, setAuthError] = useState(status === 'error');
  const popupRef = useRef<Window | null>(null);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;

      if (data.type === `${provider}_auth_success`) {
        setLoading(false);
        setAuthDone(true);
        popupRef.current = null;
        onAuthComplete(sessionId, provider);
      } else if (data.type === `${provider}_auth_error`) {
        setLoading(false);
        setAuthError(true);
        popupRef.current = null;
      }
    },
    [sessionId, provider, onAuthComplete],
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  useEffect(() => {
    if (!loading || !popupRef.current) return;
    const interval = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        setLoading(false);
        popupRef.current = null;
      }
    }, 500);
    return () => clearInterval(interval);
  }, [loading]);

  const handleClick = useCallback(async () => {
    if (loading || authDone || !vmClient) return;
    setLoading(true);
    setAuthError(false);

    try {
      const startUrl = vmClient.getAuthStartUrl(sessionId, provider, authStartUrl || undefined);
      const res = await fetch(startUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        data.auth_url,
        `${provider}_auth`,
        `width=${width},height=${height},left=${left},top=${top},popup=1`,
      );
      popupRef.current = popup;

      if (!popup) {
        setLoading(false);
        setAuthError(true);
      }
    } catch {
      setLoading(false);
      setAuthError(true);
    }
  }, [vmClient, sessionId, provider, authStartUrl, loading, authDone]);

  const paper = isPaperThemeActive();

  if (authDone) {
    if (paper) {
      return (
        <div className="flex items-center gap-3 rounded-[14px] border border-solid border-[var(--periwinkle-veil)] bg-[var(--periwinkle-wash)] px-4 py-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-solid border-[var(--periwinkle-veil)] bg-[var(--surface)]">
            <Check className="h-[22px] w-[22px] text-[var(--periwinkle)]" strokeWidth={1.8} />
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="text-[14px] text-[var(--fg)]"
              style={{ fontFamily: 'var(--font-display)', fontWeight: 400, textTransform: 'capitalize' }}
            >
              {displayName} account connected
            </div>
            <div className="text-[12px] text-[var(--fg-3)] mt-[1px]">Authentication successful</div>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.06] px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10">
          <Check className="h-4.5 w-4.5 text-emerald-400" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-medium text-zinc-200" style={{ textTransform: 'capitalize' }}>
            {displayName} account connected
          </div>
          <div className="text-[12px] text-zinc-500">Authentication successful</div>
        </div>
      </div>
    );
  }

  if (paper) {
    return (
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={handleClick}
          disabled={loading || !vmClient}
          className={cn(
            'group flex items-center gap-3 rounded-[14px] px-4 py-3 text-left',
            'bg-[var(--surface)] border border-solid transition-colors duration-200',
            authError
              ? 'border-[var(--periwinkle-veil)] hover:border-[var(--periwinkle)]'
              : 'border-[var(--hair)] hover:border-[var(--periwinkle)] hover:bg-[var(--periwinkle-wash)]',
            loading && 'cursor-wait opacity-80',
          )}
        >
          <div
            className={cn(
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px]',
              'border border-solid border-[var(--hair)] bg-[var(--bg-alt)]',
            )}
          >
            {loading ? (
              <Loader2 className="h-[22px] w-[22px] animate-spin text-[var(--fg-3)]" />
            ) : iconUrl ? (
              <ProviderIcon iconUrl={iconUrl} displayName={displayName} paper />
            ) : (
              <KeyRound className="h-[22px] w-[22px] text-[var(--fg-3)]" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="text-[14px] text-[var(--fg)]"
              style={{ fontFamily: 'var(--font-display)', fontWeight: 400 }}
            >
              {loading ? 'Waiting for authentication…' : `Sign in with ${displayName}`}
            </div>
            <div className="text-[12px] text-[var(--fg-3)] mt-[1px]">
              {authError ? 'Authentication failed — click to retry' : 'Grant access to continue'}
            </div>
          </div>
        </button>
        {authError && (
          <div className="flex items-center gap-2 px-1 text-[12px] text-[var(--fg-3)]">
            <AlertCircle className="h-3 w-3 shrink-0 text-[var(--periwinkle)]" />
            <span>Could not complete sign-in. Please try again.</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={handleClick}
        disabled={loading || !vmClient}
        className={cn(
          'flex items-center gap-3 rounded-2xl border px-4 py-3 transition-colors',
          authError
            ? 'border-red-500/20 bg-red-500/[0.04] hover:bg-red-500/[0.08]'
            : 'border-white/[0.08] bg-white/[0.04] hover:bg-white/[0.08]',
          loading && 'cursor-wait opacity-70',
        )}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/[0.06]">
          {loading ? (
            <Loader2 className="h-[22px] w-[22px] animate-spin text-zinc-400" />
          ) : iconUrl ? (
            <ProviderIcon iconUrl={iconUrl} displayName={displayName} />
          ) : (
            <KeyRound className="h-[22px] w-[22px] text-zinc-400" />
          )}
        </div>
        <div className="min-w-0 flex-1 text-left">
          <div className="text-[14px] font-medium text-zinc-200">
            {loading ? 'Waiting for authentication...' : `Sign in with ${displayName}`}
          </div>
          <div className="text-[12px] text-zinc-500">
            {authError ? 'Authentication failed — click to retry' : 'Grant access to continue'}
          </div>
        </div>
      </button>
      {authError && (
        <div className="flex items-center gap-2 px-1 text-[12px] text-red-400/80">
          <AlertCircle className="h-3 w-3 shrink-0" />
          <span>Could not complete sign-in. Please try again.</span>
        </div>
      )}
    </div>
  );
});
